import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const Confetti = ({ show, onComplete }) => {
  const [particles, setParticles] = useState([]);
  const [bursts, setBursts] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (show) {
      // Brighter, more vibrant colors for better visibility
      const colors = [
        '#3b82f6', '#10b981', '#f59e0b', '#ef4444', 
        '#8b5cf6', '#ec4899', '#06b6d4', '#f97316',
        '#22c55e', '#eab308', '#14b8a6', '#a855f7'
      ];
      const shapes = ['circle', 'square', 'triangle'];
      
      // More particles on mobile for better visibility
      const particleCount = isMobile ? 50 : 70;
      
      const newParticles = Array.from({ length: particleCount }, (_, i) => {
        const color = colors[Math.floor(Math.random() * colors.length)];
        const shape = shapes[Math.floor(Math.random() * shapes.length)];
        const size = isMobile 
          ? 10 + Math.random() * 8  // 10-18px on mobile (larger for visibility)
          : 7 + Math.random() * 5; // 7-12px on desktop
        
        // Slower on mobile for better visibility
        const duration = isMobile 
          ? 3.0 + Math.random() * 2.0  // 3-5s on mobile (slower)
          : 2.0 + Math.random() * 1.5; // 2-3.5s on desktop
        
        return {
          id: i,
          color,
          shape,
          size,
          x: Math.random() * 100,
          delay: Math.random() * 0.8,
          duration,
          rotation: Math.random() * 720 + 360, // More rotation
          xOffset: (Math.random() - 0.5) * 100, // Horizontal drift
        };
      });
      setParticles(newParticles);

      // Generate burst effects with random colors
      const burstColors = [
        { r: 59, g: 130, b: 246 },
        { r: 16, g: 185, b: 129 },
        { r: 139, g: 92, b: 246 },
        { r: 236, g: 72, b: 153 },
      ];
      const newBursts = Array.from({ length: 3 }, (_, i) => {
        const color = burstColors[Math.floor(Math.random() * burstColors.length)];
        return {
          id: i,
          color: `rgba(${color.r},${color.g},${color.b},0.5)`,
          x: (Math.random() - 0.5) * 100,
          y: (Math.random() - 0.5) * 100,
        };
      });
      setBursts(newBursts);

      const timer = setTimeout(() => {
        if (onComplete) onComplete();
      }, isMobile ? 6000 : 4000); // Longer on mobile for full animation

      return () => clearTimeout(timer);
    }
  }, [show, onComplete, isMobile]);

  if (!show) return null;

  const getShapeStyle = (particle) => {
    const baseStyle = {
      backgroundColor: particle.color,
      // Enhanced glow effect for better visibility, especially on mobile
      boxShadow: `0 0 ${particle.size * 0.8}px ${particle.color}, 
                  0 0 ${particle.size * 1.6}px ${particle.color}80, 
                  0 0 ${particle.size * 2.4}px ${particle.color}40`,
      filter: 'brightness(1.2)',
    };

    if (particle.shape === 'circle') {
      return {
        ...baseStyle,
        borderRadius: '50%',
        width: `${particle.size}px`,
        height: `${particle.size}px`,
      };
    } else if (particle.shape === 'square') {
      return {
        ...baseStyle,
        borderRadius: '2px',
        width: `${particle.size}px`,
        height: `${particle.size}px`,
      };
    } else {
      // Triangle
      return {
        width: 0,
        height: 0,
        borderLeft: `${particle.size / 2}px solid transparent`,
        borderRight: `${particle.size / 2}px solid transparent`,
        borderBottom: `${particle.size}px solid ${particle.color}`,
        boxShadow: `0 0 ${particle.size}px ${particle.color}`,
      };
    }
  };

  return (
    <div className="fixed inset-0 pointer-events-none z-[200] overflow-hidden">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          initial={{
            x: `${particle.x}vw`,
            y: '-5vh',
            rotate: 0,
            scale: 1,
            opacity: 1,
          }}
          animate={{
            x: `${particle.x}vw`,
            y: '105vh',
            rotate: particle.rotation,
            // More dramatic scale animation for visibility
            scale: [1, 1.3, 1.1, 0.9, 0],
            opacity: [1, 1, 1, 1, 0.7, 0],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            ease: [0.25, 0.46, 0.45, 0.94], // Custom easing for more natural fall
            x: {
              type: 'spring',
              stiffness: 50,
              damping: 10,
            },
          }}
          className="absolute"
          style={getShapeStyle(particle)}
        />
      ))}
      
      {/* Enhanced burst effect at the top center */}
      <motion.div
        initial={{ scale: 0, opacity: 1 }}
        animate={{ 
          scale: [0, 1.5, 2.5, 3], 
          opacity: [1, 0.9, 0.6, 0] 
        }}
        transition={{ 
          duration: isMobile ? 1.0 : 0.8, 
          ease: 'easeOut' 
        }}
        className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: isMobile ? '200px' : '150px',
          height: isMobile ? '200px' : '150px',
          background: 'radial-gradient(circle, rgba(59,130,246,0.7) 0%, rgba(16,185,129,0.5) 30%, rgba(139,92,246,0.3) 60%, transparent 100%)',
          filter: 'blur(25px) brightness(1.3)',
        }}
      />
      
      {/* Additional smaller bursts for more visual impact */}
      {bursts.map((burst) => (
        <motion.div
          key={`burst-${burst.id}`}
          initial={{ scale: 0, opacity: 0.8 }}
          animate={{ 
            scale: [0, 1.2, 1.8, 0], 
            opacity: [0.8, 0.6, 0.3, 0] 
          }}
          transition={{ 
            duration: isMobile ? 0.9 : 0.7,
            delay: burst.id * 0.1,
            ease: 'easeOut' 
          }}
          className="absolute top-1/4 left-1/2 rounded-full"
          style={{
            width: isMobile ? '120px' : '100px',
            height: isMobile ? '120px' : '100px',
            background: `radial-gradient(circle, ${burst.color} 0%, transparent 70%)`,
            filter: 'blur(15px)',
            transform: `translate(calc(-50% + ${burst.x}px), calc(-50% + ${burst.y}px))`,
          }}
        />
      ))}
    </div>
  );
};

export default Confetti;
